import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import GettingStarted from "../../../../Pages/GettingStarted";
import InvoiceListPage from "../../../../Pages/InvoiceListPage";
import LoadingPage from "../../../../Pages/LoadingPage";
import PayoutListPage from "../../../../Pages/PayoutListPage";
import CustomerListPage from "../../../../Pages/CustomerListPage";
import TransactionListPage from "../../../../Pages/TransactionListPage";
import MyConfigurationPage from "../../../../Pages/SettingsPages/MyConfigurationPage";
import { PaymentRoutes } from "../../../../Contracts/Routes";
import PaymentsIntegrationPage from "../../../../Pages/SettingsPages/PaymentsIntegrationPage";
import CustomerDetailPage from "../../../../Pages/CustomerDetailPage";
import ClientPortalLoginPage from "../../../../Pages/ClientPortalLoginPage";
import ClientPortalInvoiceListPage from "../../../../Pages/ClientPortalInvoiceListPage";
import ClientPortalCustomerListPage from "../../../../Pages/ClientPortalCustomerListPage";
import UserManagementPage from "../../../../Pages/SettingsPages/UserManagementPage";
import OnboardingStartForm from "../../../../Pages/OnboardingStartForm";
import RegisterPage from "../../../../Pages/RegisterPage";
import ZomentumGrowQuotePayPage from "../../../../Pages/ZomentumGrowQuotePayPage";
import QuickbooksSettingsPage from "../../../../Pages/SettingsPages/PaymentsIntegrationPage/QuickbooksSettingsPage";
import XeroSettingsPage from "../../../../Pages/SettingsPages/PaymentsIntegrationPage/XeroSettingsPage";
import ClientPortalCustomerDetailPage from "../../../../Pages/ClientPortalCustomerDetailPage";
import AddPaymentMethodMandatePage from "../../../../Pages/AddPaymentMethodMandatePage";
import AddPaymentMethodMandatePagePublic from "../../../../Pages/AddPaymentMethodMandatePagePublic";
import AdminTeamListPage from "../../../../Pages/AdminTeamListPage";
import CheckoutInvoicePage from "../../../../Pages/CheckoutInvoicePage";
import { useOnboardingStore } from "../../../../Store/Onboarding";
import InvoiceReceiptPage from "../../../../Pages/InvoiceReceiptPage";
import { useUserStore } from "../../../../Store/User";
import AdminTeamDetailPage from "../../../../Pages/AdminTeamDetailPage";
import AdminTeamTransactionsListPage from "../../../../Pages/AdminTeamTransactionsListPage";
import AdminTeamTransfersListPage from "../../../../Pages/AdminTeamTransfersListPage";
import PayoutDetailPage from "../../../../Pages/PayoutDetailPage";
import PayoutTroubleshootPage from "../../../../Pages/PayoutTroubleshootPage";
import InternalServerErrorPage from "../../../../Pages/ErrorPage/InternalServerErrorPage";
import SessionEndedPage from "../../../../Pages/ErrorPage/SessionEndedPage";
import InvoiceAutoCollectDetailsPage from "../../../../Pages/InvoiceAutoCollectDetailsPage";
import AcceptUserInvitePage from "../../../../Pages/AcceptUserInvitePage";
import AdminTeamUserListPage from "../../../../Pages/AdminTeamUserListPage";
import AdminTeamPayoutsListPage from "../../../../Pages/AdminTeamPayoutsListPage";
import AdminTeamPayoutTroubleshootPage from "../../../../Pages/AdminTeamPayoutTroubleshootPage";

const PaymentDashboardRoutes: React.FC = () => {
  const { isOnboardingComplete } = useOnboardingStore();
  const { currentUser } = useUserStore();

  // Partner Routes
  const GettingStartedNode: React.ReactNode = <GettingStarted />;
  const InvoiceListPageNode: React.ReactNode = <InvoiceListPage />;
  const LoadingPageNode: React.ReactNode = <LoadingPage />;
  const PayoutsPageNode: React.ReactNode = <PayoutListPage />;
  const PayoutDetailPageNode: React.ReactNode = <PayoutDetailPage />;
  const PayoutTroubleshootPageNode: React.ReactNode = <PayoutTroubleshootPage />;
  const CustomerListPageNode: React.ReactNode = <CustomerListPage />;
  const TransactionListPageNode: React.ReactNode = <TransactionListPage />;
  const MyConfigurationPageNode: React.ReactNode = <MyConfigurationPage />;
  const SettingsIntegrationPageNode: React.ReactNode = <PaymentsIntegrationPage />;
  const SettingsUserManagementPageNode: React.ReactNode = <UserManagementPage />;
  const CustomerDetailPageNode: React.ReactNode = <CustomerDetailPage />;
  const OnboardingStartFormNode: React.ReactNode = <OnboardingStartForm />;
  const RegisterPageNode: React.ReactNode = <RegisterPage />;
  const AcceptUserInvitePageNode: React.ReactNode = <AcceptUserInvitePage />;
  const AddPaymentMethodMandatePageNode: React.ReactNode = <AddPaymentMethodMandatePage />;
  const AddPaymentMethodMandatePagePublicNode: React.ReactNode = (
    <AddPaymentMethodMandatePagePublic />
  );
  const CheckoutInvoicePayPageNode: React.ReactNode = <CheckoutInvoicePage />;
  const InvoiceReceiptPageNode: React.ReactNode = <InvoiceReceiptPage />;

  // Error routes
  const InternalServerErrorPageNode: React.ReactNode = <InternalServerErrorPage />;
  const SessionEndedPageNode: React.ReactNode = <SessionEndedPage />;

  // Client Portal Routes
  const ClientPortalLoginPageNode: React.ReactNode = <ClientPortalLoginPage />;

  const ClientPortalInvoiceListPageNode: React.ReactNode = <ClientPortalInvoiceListPage />;

  const ClientPortalCustomerListPageNode: React.ReactNode = <ClientPortalCustomerListPage />;

  const ClientPortalCustomerDetailPageNode: React.ReactNode = <ClientPortalCustomerDetailPage />;

  const InvoiceAutoCollectDetailsPageNode: React.ReactNode = <InvoiceAutoCollectDetailsPage />;

  const ZomentumGrowQuotePayPageNode: React.ReactNode = <ZomentumGrowQuotePayPage />;

  const QuickbooksSettingsPageNode: React.ReactNode = <QuickbooksSettingsPage />;

  const XeroSettingsPageNode: React.ReactNode = <XeroSettingsPage />;

  const AdminTeamListPageNode = <AdminTeamListPage />;
  const AdminTeamDetailPageNode = <AdminTeamDetailPage />;
  const AdminTeamTransactionListPageNode = <AdminTeamTransactionsListPage />;
  const AdminTeamTransfersListPageNode = <AdminTeamTransfersListPage />;
  const AdminTeamUserListPageNode = <AdminTeamUserListPage />;
  const AdminTeamPayoutsListPageNode = <AdminTeamPayoutsListPage />;
  const AdminTeamPayoutTroubleshootPageNode = <AdminTeamPayoutTroubleshootPage />;
  const onboardingComplete = isOnboardingComplete();

  return (
    <Routes>
      <Route path={PaymentRoutes.GETTING_STARTED} element={GettingStartedNode} />
      <Route path={PaymentRoutes.LOADING} element={LoadingPageNode} />
      <Route path={PaymentRoutes.INTERNAL_SERVER_ERROR} element={InternalServerErrorPageNode} />
      <Route path={PaymentRoutes.SESSION_ENDED} element={SessionEndedPageNode} />
      {onboardingComplete && (
        <>
          <Route path={PaymentRoutes.CUSTOMERS} element={CustomerListPageNode} />
          <Route path={PaymentRoutes.INVOICES} element={InvoiceListPageNode} />
        </>
      )}
      <Route path={PaymentRoutes.CUSTOMERS_DETAILS} element={CustomerDetailPageNode} />
      <Route path={PaymentRoutes.PAYOUTS} element={PayoutsPageNode} />
      <Route path={PaymentRoutes.PAYOUT_DETAILS} element={PayoutDetailPageNode} />

      <Route path={PaymentRoutes.TRANSACTIONS} element={TransactionListPageNode} />
      <Route path={PaymentRoutes.SETTINGS_CONFIG} element={MyConfigurationPageNode} />
      <Route path={PaymentRoutes.SETTINGS_INTEGRATION} element={SettingsIntegrationPageNode} />
      <Route path={PaymentRoutes.SETTINGS_USERS} element={SettingsUserManagementPageNode} />
      <Route
        path={PaymentRoutes.ADD_PAYMENT_METHOD_MANDATE}
        element={AddPaymentMethodMandatePageNode}
      />
      <Route
        path={PaymentRoutes.ADD_PAYMENT_METHOD_MANDATE_PUBLIC}
        element={AddPaymentMethodMandatePagePublicNode}
      />
      <Route path={PaymentRoutes.CHECKOUT_INVOICE_PAY} element={CheckoutInvoicePayPageNode} />
      <Route path={PaymentRoutes.INVOICE_RECEIPT} element={InvoiceReceiptPageNode} />
      <Route path={PaymentRoutes.CLIENT_PORTAL_LOGIN} element={ClientPortalLoginPageNode} />
      <Route
        path={PaymentRoutes.CLIENT_PORTAL_INVOICES}
        element={ClientPortalInvoiceListPageNode}
      />
      <Route
        path={PaymentRoutes.CLIENT_PORTAL_CUSTOMERS}
        element={ClientPortalCustomerListPageNode}
      />
      <Route
        path={PaymentRoutes.CLIENT_PORTAL_CUSTOMERS_DETAILS}
        element={ClientPortalCustomerDetailPageNode}
      />
      <Route
        path={PaymentRoutes.INVOICE_AUTO_COLLECT_DETAIL}
        element={InvoiceAutoCollectDetailsPageNode}
      />
      <Route path={PaymentRoutes.ONBOARDING_FORM} element={OnboardingStartFormNode} />
      <Route path={PaymentRoutes.ZOMENTUM_GROW_QUOTE_PAY} element={ZomentumGrowQuotePayPageNode} />
      <Route path={PaymentRoutes.QUICKBOOKS_SETTINGS} element={QuickbooksSettingsPageNode} />
      <Route path={PaymentRoutes.XERO_SETTINGS} element={XeroSettingsPageNode} />
      <Route path={PaymentRoutes.REGISTER} element={RegisterPageNode} />
      <Route path={PaymentRoutes.ACCEPT_USER_INVITE} element={AcceptUserInvitePageNode} />
      {currentUser?.is_admin && (
        <>
          <Route path={PaymentRoutes.ADMIN_TEAMS} element={AdminTeamListPageNode} />
          <Route path={PaymentRoutes.ADMIN_TEAM_DETAIL} element={AdminTeamDetailPageNode} />
          <Route
            path={PaymentRoutes.ADMIN_TEAM_TRANSACTION_LIST}
            element={AdminTeamTransactionListPageNode}
          />
          <Route
            path={PaymentRoutes.ADMIN_TEAM_TRANSFERS_LIST}
            element={AdminTeamTransfersListPageNode}
          />
          <Route path={PaymentRoutes.PAYOUT_TROUBLESHOOT} element={PayoutTroubleshootPageNode} />
          <Route path={PaymentRoutes.ADMIN_TEAM_USER_LIST} element={AdminTeamUserListPageNode} />
          <Route path={PaymentRoutes.ADMIN_TEAM_PAYOUTS_LIST} element={AdminTeamPayoutsListPageNode} />
          <Route
            path={PaymentRoutes.ADMIN_TEAM_PAYOUT_TROUBLESHOOT}
            element={AdminTeamPayoutTroubleshootPageNode}
          />   
        </>
      )}
      <Route
        path="*"
        element={
          <Navigate
            to={onboardingComplete ? PaymentRoutes.CUSTOMERS : PaymentRoutes.GETTING_STARTED}
            replace={true}
          />
        }
      />
    </Routes>
  );
};

export default PaymentDashboardRoutes;
